<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import Toast, { type ToastMessageOptions } from 'primevue/toast';
import { onMounted } from 'vue';
import { onMessageListener } from './services/firebase';
import { useAppStore } from './stores/app';
import { prepareToast } from './models/toast';
import type { MessagePayload } from 'firebase/messaging';
import { useRouter } from 'vue-router';
import SpData from '@/assets/sp-data-icon.svg';
import { useToast } from 'primevue/usetoast';
import { transformDeepLink } from './utils';
const authStore = useAuthStore();
authStore.checkAuth();
const appStore = useAppStore();
const toast = useToast();


const userStore = useUserStore();
if (authStore.userLoggedIn) {
  userStore.getUser(authStore.userData!.identifier!);
}

const router = useRouter();

const viewDetails = (url: string | undefined, message: ToastMessageOptions) => {
  if (url) {
    router.replace(url);
    toast.remove(message);
  }
};


onMounted(() => {
  onMessageListener((payload: MessagePayload) => {

    if (payload.notification) {
      const toastData = prepareToast(
        'success', 
        payload.notification?.title ?? 'notification title', 
        payload.notification?.body ?? 'notification body',
        10000
      );

      toastData.closable = false;

      let finalUrl = undefined;
      if (payload.data?.url) {
        // Transform the incoming URL into the desired path format
        finalUrl = transformDeepLink(payload.data.url);
      }
      
      // Add a 'detail' object to signal a custom toast
      // @ts-expect-error - We can ignore this type error since we're adding a custom property
      toastData.data = { 
        type: 'push-notification',
        // You can pass any other custom data here
        ...payload.data,
        url: finalUrl
      };

      appStore.addToast(toastData);
    }
  });

  if ('serviceWorker' in navigator && navigator.serviceWorker?.addEventListener) {
    // Listen to service worker messages sent via postMessage()
    navigator.serviceWorker.addEventListener('message', event => {
      if (!event.data.action) {
        return;
      }

      switch (event.data.action) {
        case 'redirect-from-notificationclick':
          router.replace(event.data.url);
          break;
        // no default
      }
    });
  }
});
</script>

<template>
  <Toast>
    <template #message="slotProps">
      <!-- Check for the custom 'push-notification' type -->
      

      <div v-if="slotProps.message.data?.type === 'push-notification'" class="toast-wrapper">
        <div class="toast-accent"></div>

        <div class="toast-icon">
          <img alt="Studypages Data" :src="SpData" />
        </div>

        <div class="toast-content">
          <h3 class="toast-title">{{slotProps.message.summary}}</h3>
          <p class="toast-message">{{slotProps.message.detail}}</p>
          
          <button class="toast-action" @click="viewDetails(slotProps.message.data.url, slotProps.message)" v-if="slotProps.message.data?.url">
            View
          </button>
        </div>

        <button class="toast-close" aria-label="Close notification" @click="toast.remove(slotProps.message)">
          <FontAwesomeIcon :icon="['fas', 'close']" class="gallery-close" />
        </button>
      </div>

      <!-- ELSE: Render a template that mimics the default PrimeVue toast -->
      <div v-else class="p-toast-message-content">
        <span :class="['p-toast-message-icon', 'pi', slotProps.message.severity ? `pi-check-circle` : 'pi-info-circle']"></span>
        <div class="p-toast-message-text">
          <div class="p-toast-summary">{{ slotProps.message.summary }}</div>
          <div class="p-toast-detail">{{ slotProps.message.detail }}</div>
        </div>
      </div>
    </template>
  </Toast>
  <RouterView />
</template>

<style lang="scss">
/* Not using 'scoped' here, when using scoped properties for toast, they are not working. */
.p-toast {
  font-size: 14px;
}
.p-toast-message-text {
  margin: 0 0 0 0.5rem;
  line-height: 1.2;
}
.p-toast-summary {
  font-weight: 500;
}
.p-toast-message-icon {
  height: 16px;
  width: 16px;
}
.p-toast-detail {
  margin-top: 8px;
}
.p-toast-icon-close {
  height: 16px;
  width: 16px;
  outline-color: transparent;
}
.grecaptcha-badge { 
  visibility: hidden !important;
}
.p-galleria-close-button {
  z-index: 9999 !important;
  background-color: lightgray !important;
}
.toast-wrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 500px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px 32px 24px 28px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.toast-accent {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 60%;
  width: 6px;
  background-color: #3B82F6; /* Bright blue */
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.toast-icon {
  flex-shrink: 0;
  margin-right: 16px;
  margin-top: -2px; /* Slight optical adjustment */
}

.toast-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.toast-title {
  margin: 0 0 6px 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.toast-message {
  margin: 0 0 12px 0;
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
}

.toast-action {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 15px;
  color: #6B7280;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.toast-action:hover {
  color: #374151;
}

.toast-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 26px;
  height: 26px;
  background-color: #F3F4F6;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toast-close:hover {
  background-color: #E5E7EB;
}

.p-toast .p-toast-message:has(.toast-wrapper) {
  /* Remove background and border from the main message container */
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.p-toast .p-toast-message:has(.toast-wrapper) .p-toast-message-content {
  /* Remove padding from the inner content container */
  padding: 0 !important;
}

.p-toast-message-content {
  justify-content: space-between;
}
</style>
